import { t, Trans } from "@lingui/macro";
import { CheckCircleIcon, EnvelopeSimpleIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useMemo, useState } from "react";

import { useCoverLetterAccess } from "../../documents/cover-letters/use-cover-letter-access";
import { DashboardContent } from "../../components/layout/dashboard-content";
import { TemplateCard } from "../components/template-card";
import { TemplatesPageHeader } from "../components/templates-page-header";
import {
  COVER_LETTER_TEMPLATE_CATEGORIES,
  COVER_LETTER_TEMPLATES,
  filterTemplates,
  type DocumentTemplate,
  type TemplateCategory,
} from "../data/templates";

export const CoverLetterTemplatesPage = () => {
  const { requestAccess, pending, accessDialog } = useCoverLetterAccess();
  const [selectedCategory, setSelectedCategory] = useState<TemplateCategory>("all");

  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null);

  const visibleTemplates = useMemo(
    () => filterTemplates(COVER_LETTER_TEMPLATES, selectedCategory),
    [selectedCategory],
  );

  const handleSelectTemplate = (template: DocumentTemplate) => {
    void requestAccess(() => setSelectedTemplate(template));
  };

  return (
    <DashboardContent>
      <section className="min-w-0 space-y-5">
        <TemplatesPageHeader
          title="Modèles de lettres de motivation"
          description="Choisissez une présentation adaptée à votre prochaine candidature."
          categories={COVER_LETTER_TEMPLATE_CATEGORIES}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />

        <p role="status" className="text-sm text-slate-600">
          {pending
            ? t`Vérification de votre pack…`
            : t`La création de lettres nécessite un pack actif. L’éditeur est en préparation.`}
        </p>
        {selectedTemplate && (
          <div
            role="status"
            className={cn(
              "flex items-start gap-2.5 rounded-xl",
              "border border-emerald-200 bg-emerald-50",
              "px-4 py-3",
            )}
          >
            <CheckCircleIcon
              aria-hidden
              size={18}
              weight="fill"
              className="mt-0.5 shrink-0 text-emerald-600"
            />

            <div className="min-w-0">
              <p className="text-xs font-semibold text-emerald-900">
                Modèle « {selectedTemplate.name} » sélectionné
              </p>

              <p className="mt-0.5 text-[11px] leading-4 text-emerald-700">
                <Trans>
                  Votre pack est actif. L’éditeur de lettres de motivation est encore en
                  préparation.
                </Trans>
              </p>
            </div>
          </div>
        )}

        {visibleTemplates.length > 0 ? (
          <div
            className={cn(
              "grid grid-cols-2 gap-2.5",
              "sm:gap-4",
              "md:grid-cols-3",
              "2xl:grid-cols-4",
            )}
          >
            {visibleTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                selected={selectedTemplate?.id === template.id}
                onSelect={handleSelectTemplate}
              />
            ))}
          </div>
        ) : (
          <div className="grid min-h-52 place-items-center rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center">
            <div>
              <span className="mx-auto grid size-11 place-items-center rounded-xl bg-indigo-50 text-indigo-600">
                <EnvelopeSimpleIcon aria-hidden size={23} weight="duotone" />
              </span>

              <p className="mt-3 text-[13px] font-semibold text-indigo-950">
                Aucun modèle disponible
              </p>

              <p className="mt-1 text-xs text-slate-500">Essayez une autre catégorie.</p>
            </div>
          </div>
        )}
      </section>
      {accessDialog}
    </DashboardContent>
  );
};
